import { useQuery } from "@apollo/client";
import Sidebar from "components/sidebar/Sidebar";
import { useAppContext } from "contexts/AppContext";
import React, { useEffect, useState } from "react";
import { Badge, Button, Col, Container, Image, Row } from "react-bootstrap";
import { Cart, Ethernet, Gear, List } from "react-bootstrap-icons";
import resourcesgql from "gql/dashboard/common/resources.gql";
import { Outlet, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Settings from "components/Settings";
import { useIndexedDB } from "react-indexed-db-hook";
import useSync from "hooks/useSync";
import humps from "humps";
import LogoLoading from "components/LogoLoading";
import useResources from "hooks/useResources";

const AppLayout = () => {
  // contexts
  const { appState, setAppState } = useAppContext();

  // states
  const [profileModal, setProfileModal] = useState(false);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);

  // hooks
  const { sync } = useSync();
  const { makeAccounting, makeItemMasterData, makeContacts } = useResources();
  const { loading, data, error } = useQuery(resourcesgql, {
    skip: !appState.settings.internet.online,
  });
  const db = useIndexedDB("appState");
  const navigate = useNavigate();

  // functions
  const sort = (arr, key = "name", desc = false) => {
    const i = desc ? -1 : 1;
    return arr.sort((a, b) => (a[key] > b[key] ? i : -i));
  };

  const continueOrder = () => {
    navigate(`/dashboard/sales/orders/form?${appState.lastOrder}`);
  };

  // effects
  useEffect(() => {
    if (!loading && data) {
      const camelizedData = humps.camelizeKeys(data);

      // maintaining accounting
      const { paymentTerms, taxes, currencies } = makeAccounting(
        camelizedData.accounting
      );

      // maintaining item master data
      const { items, salesItems, purchaseItems, itemGroups, uoms, itemTags } =
        makeItemMasterData(camelizedData.itemMasterData, taxes);

      // maintaining contacts data
      const { companies, customers, suppliers } = makeContacts(
        camelizedData.contacts,
        paymentTerms,
        currencies
      );

      setAppState({
        key: "resources",
        value: {
          users: sort(data.allUsers, "name"),
          paymentTerms,
          currencies,
          taxes,
          companies,
          customers,
          suppliers,
          items,
          salesItems,
          purchaseItems,
          uoms,
          itemTags,
          itemGroups: itemGroups.filter((x) => x.active),
        },
      });
    } else if (!loading && error) {
      toast.error(
        "Resources failed to load. Try again or contact system admin."
      );
    }
  }, [loading]);

  useEffect(() => {
    if (appState.resources) setResourcesLoaded(true);
  }, [appState.resources]);

  useEffect(() => {
    db.update({
      id: 1,
      ...appState,
      settings: { ...appState.settings, moreSettings: null },
    });
  }, [appState]);

  useEffect(() => {
    setAppState({
      key: "lastOrder",
      value: localStorage.getItem("last-order"),
    });
  }, []);

  return (
    <React.Fragment>
      <Settings />
      {appState.lastOrder &&
        location.pathname !== "/dashboard/sales/orders/form" && (
          <Button
            variant="danger"
            className="position-fixed rounded-circle"
            onClick={continueOrder}
            style={{
              bottom: "80px",
              left: "20px",
              width: "50px",
              height: "50px",
              zIndex: 10,
            }}
          >
            <Cart size={25} color="white" />
          </Button>
        )}
      <Container
        fluid
        className="h-fit-content pb-3 position-sticky top-0 bg-main"
        style={{ zIndex: 9, minHeight: "100vh" }}
      >
        <Sidebar show={profileModal} setShow={setProfileModal} />
        <Row
          id="topbar-container"
          className="justify-content-between border-bottom align-items-center position-sticky bg-white top-0 py-2"
          style={{ zIndex: 19 }}
        >
          <Col xs={4} className="d-flex justify-content-start">
            <List
              className="w-auto"
              size="30"
              onClick={() => setProfileModal(true)}
            />
            {!appState.settings.internet.online && (
              <Badge className="bg-danger text-white m-0 align-self-center rounded-2 p-1 fw-bold">
                Offline Mode
              </Badge>
            )}
            {appState.settings.internet.online &&
              appState.settings.internet.quality === "Poor" && (
                <Badge className="bg-warning text-black m-0 align-self-center rounded-2 p-1 fw-bold">
                  <Ethernet size={16} />
                  <span className="mx-1">Poor connection</span>
                </Badge>
              )}
          </Col>
          <Col xs={4} className="d-flex justify-content-center">
            <Image
              style={{ width: "100px" }}
              src={appState.auth.user?.entity.logo != "" ? appState.auth.user?.entity.logo : undefined}
            />
          </Col>
          <Col xs={4} className="d-flex justify-content-end">
            <Gear
              size="30"
              className="w-auto"
              onClick={() => setAppState({ key: "settings.show", value: true })}
            />
          </Col>
        </Row>
        {!resourcesLoaded && <LogoLoading text="Loading Resources..." />}
        {resourcesLoaded && <Outlet />}
      </Container>
    </React.Fragment>
  );
};

export default AppLayout;
